import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    Container,
    Divider,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useInnsynContext } from '../../../../context/InnsynContextProvider'
import { ExpandMore } from '@mui/icons-material'
import {
    INNSYN_SEARCH_OEREMERKE_OMRAADE_PATH,
    INNSYN_SEARCH_OEREMERKE_PATH,
} from '../../../../paths'
import { FormattedMessage, useIntl } from 'react-intl'
import NavigationHeader from '../../../../components/NavigationHeader'
import '../../UseButton.scss'
import { getOmraadeNameKey } from '../../../../services/TextService'

export const SelectDistrict = () => {
    const navigate = useNavigate()
    const [query, setQuery] = useSearchParams()
    const { geografi } = useInnsynContext()
    const { areaId } = useParams()
    const intl = useIntl()
    const [selectedArea, setSelectedArea] = useState<Area | undefined>(
        undefined
    )
    const [allSelected, setAllSelected] = useState(false)
    const areaStringArray = query.getAll('a')
    const districtStringArray = query.getAll('d')
    const siidaStringArray = query.getAll('s')
    const { state } = useLocation() as RightEar

    const cancelAndClearOperation = ({
        clear = true,
    }: { clear?: boolean } = {}) => {
        navigate(
            {
                pathname: INNSYN_SEARCH_OEREMERKE_PATH,
                search: !clear ? `?${query}` : undefined,
            },
            { replace: true }
        )
    }

    useEffect(() => {
        const area =
            geografi && geografi.find((el) => el.omraadeId === Number(areaId))
        if (!area) {
            console.warn(
                `No matching area with id ${areaId} was found.\nRedirecting to tag search.`
            )
            cancelAndClearOperation()
            return
        }
        setSelectedArea(area)
    }, [])

    useEffect(() => {
        const isAllChecked = getAllDistrictsChecked()
        selectedArea && setAllSelected(!!isAllChecked)
    }, [selectedArea, query])

    const handleAreaLogic = (
        event: React.ChangeEvent<HTMLInputElement>,
        district: District
    ) => {
        const districtGettingChecked = event.target.checked
        if (districtGettingChecked) {
            if (
                selectedArea &&
                !areaStringArray.includes(selectedArea.omraadeId.toString())
            ) {
                query.append('a', selectedArea.omraadeId.toString())
            }
        } else {
            const noDistrictInQuery = selectedArea?.distrikter.every(
                (dist) =>
                    !query
                        .getAll('d')
                        .filter(
                            (districtString) =>
                                districtString !==
                                district.distriktId.toString()
                        )
                        .includes(dist.distriktId.toString())
            )
            if (noDistrictInQuery) {
                let areasToKeep = areaStringArray.filter(
                    (it) => it !== selectedArea?.omraadeId.toString()
                )
                query.delete('a')
                areasToKeep.forEach((item) => query.append('a', item))
            }
        }
        setQuery(query, { replace: true })
    }

    function removeSelectedAreaDistrictsFromDistrictArray() {
        return districtStringArray.filter(
            (el) =>
                !selectedArea?.distrikter.some(
                    (district) => district.distriktId.toString() === el
                )
        )
    }

    const handleDistrictLogic = (
        event: React.ChangeEvent<HTMLInputElement>,
        district: District
    ) => {
        const gettingChecked = event.target.checked
        if (gettingChecked) {
            const isEveryDistrictInQuery = selectedArea?.distrikter.every(
                (dist) =>
                    [
                        ...districtStringArray,
                        district.distriktId.toString(),
                    ].includes(dist.distriktId.toString())
            )
            if (isEveryDistrictInQuery) {
                const districtsToKeep =
                    removeSelectedAreaDistrictsFromDistrictArray()
                query.delete('d')
                districtsToKeep?.forEach((item) =>
                    query.append('d', item.toString())
                )
            } else {
                query.append('d', district.distriktId.toString())
            }
        } else {
            const noDistrictInQuery =
                selectedArea?.distrikter.every(
                    (dist) =>
                        ![...districtStringArray].includes(
                            dist.distriktId.toString()
                        )
                ) && siidaStringArray.length === 0
            if (noDistrictInQuery) {
                let districtsToKeep = districtStringArray
                const ourDistricts = selectedArea?.distrikter
                    .filter((it) => it.distriktId !== district.distriktId)
                    .map((it) => it.distriktId.toString())
                if (ourDistricts) {
                    districtsToKeep = districtsToKeep.concat(ourDistricts)
                }
                query.delete('d')
                districtsToKeep.forEach((item) => query.append('d', item))
            } else {
                let districtsToKeep = districtStringArray.filter(
                    (it) => it !== district.distriktId.toString()
                )
                query.delete('d')
                districtsToKeep.forEach((item) => query.append('d', item))
            }
        }
        setQuery(query, { replace: true })
        handleAreaLogic(event, district)
    }

    const handleSidaLogic = (
        event: React.ChangeEvent<HTMLInputElement>,
        district: District,
        siida: Siida
    ) => {
        const gettingChecked = event.target.checked
        if (gettingChecked) {
            const isEverySiidaInQuery = district.siidaer.every((el) =>
                [...siidaStringArray, siida.siidaId.toString()].includes(
                    el.siidaId.toString()
                )
            )
            if (isEverySiidaInQuery) {
                const siidasToKeep = siidaStringArray.filter(
                    (el) =>
                        !district.siidaer.some(
                            (sid) => sid.siidaId.toString() === el
                        )
                )
                query.delete('s')
                siidasToKeep?.forEach((item) =>
                    query.append('s', item.toString())
                )
                setQuery(query, { replace: true })
                handleDistrictLogic(event, district)
            } else {
                query.append('s', siida.siidaId.toString())
            }
        } else {
            const noSiidaInQuery = district.siidaer.every(
                (s) => ![...siidaStringArray].includes(s.siidaId.toString())
            )
            if (noSiidaInQuery) {
                let siidasToKeep = siidaStringArray
                const ourSiidas = district.siidaer
                    .filter((it) => it.siidaId !== siida.siidaId)
                    .map((it) => it.siidaId.toString())
                if (ourSiidas) {
                    siidasToKeep = siidasToKeep.concat(ourSiidas)
                }
                query.delete('s')
                siidasToKeep.forEach((item) => query.append('s', item))
                setQuery(query, { replace: true })
                handleDistrictLogic(event, district)
            } else {
                let siidasToKeep = siidaStringArray.filter(
                    (it) => it !== siida.siidaId.toString()
                )
                query.delete('s')
                siidasToKeep.forEach((item) => query.append('s', item))
            }
        }
        setQuery(query, { replace: true })
    }

    const handleAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const gettingChecked = event.target.checked
        if (gettingChecked) {
            if (
                selectedArea &&
                !areaStringArray.includes(selectedArea.omraadeId.toString())
            ) {
                query.append('a', selectedArea.omraadeId.toString())
            }
            const districtsToKeep =
                removeSelectedAreaDistrictsFromDistrictArray()
            const siidasToKeep = siidaStringArray.filter(
                (el) =>
                    !selectedArea?.distrikter.some((dis) =>
                        dis.siidaer.some((sid) => sid.siidaId.toString() === el)
                    )
            )
            query.delete('d')
            query.delete('s')
            districtsToKeep.forEach((item) => query.append('d', item))
            siidasToKeep.forEach((item) => query.append('s', item))
        } else {
            let areasToKeep = areaStringArray.filter(
                (it) => it !== selectedArea?.omraadeId.toString()
            )
            query.delete('a')
            areasToKeep.forEach((item) => query.append('a', item))
        }
        setQuery(query, {
            replace: true,
            state: { isRightEar: state?.isRightEar },
        })
    }

    const handleAllSiidaChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        district: District
    ) => {
        handleDistrictLogic(event, district)
        query.delete('s')
        setQuery(query, {
            replace: true,
            state: { isRightEar: state?.isRightEar },
        })
    }

    const getAllDistrictsChecked = () => {
        const noDistrictInDistrictArray = !selectedArea?.distrikter.some(
            (distrikt) =>
                districtStringArray.includes(distrikt.distriktId.toString())
        )
        if (
            selectedArea?.distrikter.some(
                (distrikt) => distrikt.siidaer.length > 0
            )
        ) {
            return (
                selectedArea &&
                areaStringArray.includes(selectedArea.omraadeId.toString()) &&
                noDistrictInDistrictArray &&
                siidaStringArray.length === 0
            )
        }
        return (
            selectedArea &&
            areaStringArray.includes(selectedArea.omraadeId.toString()) &&
            noDistrictInDistrictArray
        )
    }

    function compareDistrictNames(d1: District, d2: District) {
        return d1.distriktNavn > d2.distriktNavn ? 1 : -1
    }

    function getSiidaChecked(distrikt: District, siida: Siida) {
        return (
            (allSelected && siidaStringArray.length === 0) ||
            districtStringArray.includes(distrikt.distriktId.toString()) ||
            siidaStringArray.includes(siida.siidaId.toString())
        )
    }

    function getDistrictWithSiidasChecked(distrikt: District) {
        return (
            allSelected ||
            districtStringArray.includes(distrikt.distriktId.toString()) ||
            distrikt.siidaer.some((s) =>
                siidaStringArray.includes(s.siidaId.toString())
            )
        )
    }

    return (
        <Container
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}>
            <NavigationHeader
                onClickBack={() =>
                    navigate(
                        {
                            pathname: INNSYN_SEARCH_OEREMERKE_OMRAADE_PATH,
                            search: `?${query}`,
                        },
                        { state: { isRightEar: state?.isRightEar } }
                    )
                }
            />
            <Box
                sx={{
                    marginTop: '1rem',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                <div style={{ fontWeight: '400', fontSize: '1.25rem' }}>
                    {intl.formatMessage({
                        id: getOmraadeNameKey(selectedArea?.omraadeId),
                    })}
                </div>
                <List
                    component='nav'
                    aria-label={
                        'liste over alle reindistrikter i' +
                        selectedArea?.omraadeNavn
                    }
                    sx={{ marginBottom: '20%' }}>
                    <>
                        <Stack direction='row'>
                            <Checkbox
                                inputProps={{
                                    'aria-label': `Velg alle`,
                                }}
                                checked={allSelected}
                                onChange={(event) => handleAllChange(event)}
                            />
                            <ListItem button>
                                <ListItemText
                                    primary={
                                        <FormattedMessage
                                            id={'search-page.choose-all'}
                                        />
                                    }
                                />
                            </ListItem>
                        </Stack>
                        <Divider />
                    </>
                    {selectedArea &&
                        selectedArea.distrikter
                            .sort(compareDistrictNames)
                            .map((distrikt, index) => (
                                <div key={'distrikt-' + index}>
                                    <Stack direction='row'>
                                        {distrikt.siidaer?.length > 0 ? (
                                            <Accordion
                                                disableGutters={true}
                                                sx={{
                                                    boxShadow: 0,
                                                    width: '100%',
                                                }}>
                                                <AccordionSummary
                                                    expandIcon={<ExpandMore />}
                                                    aria-controls='distrikt-kárásjoga-klikk-for-å-velge-siidaer'
                                                    id='panel1a-header'
                                                    sx={{ padding: 0 }}>
                                                    <Checkbox
                                                        inputProps={{
                                                            'aria-label': `Velg alle siidaer i ${distrikt.distriktNavn}`,
                                                        }}
                                                        checked={getDistrictWithSiidasChecked(
                                                            distrikt
                                                        )}
                                                        onClick={(event) => {
                                                            event.stopPropagation()
                                                        }}
                                                        onChange={(event) =>
                                                            handleAllSiidaChange(
                                                                event,
                                                                distrikt
                                                            )
                                                        }
                                                    />
                                                    <ListItem>
                                                        <ListItemText
                                                            primary={
                                                                distrikt.distriktNavn
                                                            }
                                                        />
                                                    </ListItem>
                                                </AccordionSummary>
                                                <AccordionDetails
                                                    sx={{
                                                        backgroundColor:
                                                            '#E3EDF0',
                                                    }}>
                                                    <Stack
                                                        sx={{
                                                            alignItems: 'start',
                                                        }}>
                                                        {distrikt.siidaer.map(
                                                            (siida, index) => (
                                                                <Stack
                                                                    key={
                                                                        'siida-' +
                                                                        index
                                                                    }
                                                                    direction='row'>
                                                                    <Checkbox
                                                                        inputProps={{
                                                                            'aria-label': `Velg ${siida.siidaNavn}`,
                                                                        }}
                                                                        checked={getSiidaChecked(
                                                                            distrikt,
                                                                            siida
                                                                        )}
                                                                        onChange={(
                                                                            event
                                                                        ) => {
                                                                            handleSidaLogic(
                                                                                event,
                                                                                distrikt,
                                                                                siida
                                                                            )
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        sx={{
                                                                            marginTop:
                                                                                '0.625rem',
                                                                        }}>
                                                                        {
                                                                            siida.siidaNavn
                                                                        }
                                                                    </Typography>
                                                                </Stack>
                                                            )
                                                        )}
                                                    </Stack>
                                                </AccordionDetails>
                                            </Accordion>
                                        ) : (
                                            <>
                                                <Checkbox
                                                    inputProps={{
                                                        'aria-label': `Velg ${distrikt.distriktNavn}`,
                                                    }}
                                                    checked={
                                                        allSelected ||
                                                        districtStringArray.includes(
                                                            distrikt.distriktId.toString()
                                                        )
                                                    }
                                                    onChange={(event) =>
                                                        handleDistrictLogic(
                                                            event,
                                                            distrikt
                                                        )
                                                    }
                                                />
                                                <ListItem
                                                    button
                                                    onClick={() => ({})}>
                                                    <ListItemText
                                                        primary={
                                                            distrikt.distriktNavn
                                                        }
                                                    />
                                                </ListItem>
                                            </>
                                        )}
                                    </Stack>
                                    <Divider />
                                </div>
                            ))}
                </List>
            </Box>
            <Button
                size='medium'
                variant='contained'
                className='useButton'
                sx={{ position: 'fixed', marginBottom: '2rem' }}
                onClick={() => {
                    navigate(
                        {
                            pathname: INNSYN_SEARCH_OEREMERKE_OMRAADE_PATH,
                            search: `?${query}`,
                        },
                        {
                            replace: true,
                            state: { isRightEar: state?.isRightEar },
                        }
                    )
                }}>
                <FormattedMessage id={'search-page.use'} />
            </Button>
        </Container>
    )
}

export default SelectDistrict
